import { faUserPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { login } from '@services/auth'
import { getCookie } from '@services/http'
import { type FormEvent, useState } from 'react'
import useSignIn from 'react-auth-kit/hooks/useSignIn'
import { useNavigate } from 'react-router-dom'
import type { AuthError } from '@/types/authTypes'
import { getFieldError, validateEmail } from '@/utils/validation'
import type { ValidationError } from '@/types/validationTypes'

const Auth = () => {
  const navigate = useNavigate()
  const signIn = useSignIn()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<AuthError | null>(null)
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    []
  )

  const validateForm = () => {
    const errors: ValidationError[] = []
    const emailError = validateEmail(email)
    const passwordError =
      !password
        ? 'Password is required'
        : password.length < 8
        ? 'Password must be at least 8 characters long'
        : null

    if (emailError) errors.push({ field: 'email', message: emailError })
    if (passwordError)
      errors.push({ field: 'password', message: passwordError })

    setValidationErrors(errors)
    return errors.length === 0
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    if (validationErrors.some((err) => err.field === 'email')) {
      const emailError = validateEmail(e.target.value)
      if (emailError) {
        setValidationErrors((prev) => {
          const filtered = prev.filter((e) => e.field !== 'email')
          return [...filtered, { field: 'email', message: emailError }]
        })
      } else {
        setValidationErrors((prev) => prev.filter((e) => e.field !== 'email'))
      }
    }
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
    if (validationErrors.some((err) => err.field === 'password')) {
      const passwordError =
        !e.target.value
          ? 'Password is required'
          : e.target.value.length < 8
          ? 'Password must be at least 8 characters long'
          : null
      if (passwordError) {
        setValidationErrors((prev) => {
          const filtered = prev.filter((e) => e.field !== 'password')
          return [...filtered, { field: 'password', message: passwordError }]
        })
      } else {
        setValidationErrors((prev) =>
          prev.filter((e) => e.field !== 'password')
        )
      }
    }
  }

  const handleEmailBlur = () => {
    const emailError = validateEmail(email)
    if (emailError) {
      setValidationErrors((prev) => {
        const filtered = prev.filter((e) => e.field !== 'email')
        return [...filtered, { field: 'email', message: emailError }]
      })
    } else {
      setValidationErrors((prev) => prev.filter((e) => e.field !== 'email'))
    }
  }

  const handlePasswordBlur = () => {
    const passwordError =
      !password
        ? 'Password is required'
        : password.length < 8
        ? 'Password must be at least 8 characters long'
        : null
    if (passwordError) {
      setValidationErrors((prev) => {
        const filtered = prev.filter((e) => e.field !== 'password')
        return [...filtered, { field: 'password', message: passwordError }]
      })
    } else {
      setValidationErrors((prev) => prev.filter((e) => e.field !== 'password'))
    }
  }

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      const response = await login(email, password)
      const token = getCookie('jwt_token')

      if (!token) {
        throw new Error('No authentication token received')
      }

      signIn({
        auth: {
          token,
        },
        userState: {
          email,
          user_id: response.user_id,
          avatar: response.avatar,
        },
      })

      navigate('/playground/channel')
    } catch (err) {
      setError(err as AuthError)
    } finally {
      setIsLoading(false)
    }
  }

  const handleRegister = () => {
    navigate('register')
  }

  return (
    <div className="relative w-full max-w-md max-h-max mx-auto my-auto p-6 border border-gray-200 rounded-lg shadow-lg bg-gradient-to-r from-blue-50 to-gray-50">
      <h1 className="text-secondary mb-6 text-4xl font-bold text-center">
        Authentication
      </h1>

      {error && (
        <div className="text-red-500 mb-4 text-center p-3 bg-red-50 rounded-md border border-red-200">
          {error.message}
        </div>
      )}

      <form
        className="flex flex-col items-center w-full"
        onSubmit={handleLogin}
      >
        <div className="w-full max-w-sm">
          <input
            className="mb-4 p-3 text-2xl border border-gray-300 rounded-md w-full focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none transition-all duration-200"
            placeholder="Email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            disabled={isLoading}
          />
          {getFieldError(validationErrors, 'email') && (
            <span className="text-red-500 text-sm mb-2 block">
              {getFieldError(validationErrors, 'email')}
            </span>
          )}
        </div>
        <div className="w-full max-w-sm">
          <input
            className="mb-4 p-3 text-2xl border border-gray-300 rounded-md w-full focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none transition-all duration-200"
            type="password"
            placeholder="Password"
            value={password}
            onChange={handlePasswordChange}
            onBlur={handlePasswordBlur}
            disabled={isLoading}
          />
          {getFieldError(validationErrors, 'password') && (
            <span className="text-red-500 text-sm mb-2 block">
              {getFieldError(validationErrors, 'password')}
            </span>
          )}
        </div>
        <button
          type="submit"
          className="bg-primary p-4 my-4 text-secondary font-normal text-2xl rounded-md cursor-pointer hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed w-full max-w-sm transition-colors duration-300 shadow-md"
          disabled={isLoading}
        >
          {isLoading ? 'Logging in...' : 'Login'}
        </button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-gray-600 mb-2 text-xl">Don't have an account?</p>
        <button
          onClick={handleRegister}
          className="flex items-center justify-center mx-auto text-secondary hover:text-secondary-dark transition-colors duration-300 text-xl"
        >
          <FontAwesomeIcon icon={faUserPlus} className="mr-2 h-6 w-6" />
          <span>Register Now</span>
        </button>
      </div>
    </div>
  )
}

export default Auth
